import { FormEvent, useEffect, useState } from "react";
import { AiCallEstimate, api, DocStatus, Document, DocumentPage } from "../lib/api";

export type DocStatusFilter = "all" | DocStatus;

export interface DocFilters {
  q: string;
  status: DocStatusFilter;
  /** 1-indexed for the URL/UI; converted to offset on the wire. */
  page: number;
}

export const DEFAULT_DOC_FILTERS: DocFilters = { q: "", status: "all", page: 1 };
export const DOC_PAGE_SIZE = 25;

const STATUS_TABS: { value: DocStatusFilter; label: string }[] = [
  { value: "all",        label: "All" },
  { value: "ready",      label: "Ready" },
  { value: "processing", label: "Processing" },
  { value: "queued",     label: "Queued" },
  { value: "failed",     label: "Failed" },
];

interface Props {
  caseId: number;
  page: DocumentPage | null;
  filters: DocFilters;
  pageSize: number;
  busy?: boolean;
  onFiltersChange: (patch: Partial<DocFilters>) => void;
  onChanged: () => void;
}

export default function DocList({
  caseId,
  page,
  filters,
  pageSize,
  busy = false,
  onFiltersChange,
  onChanged,
}: Props) {
  const [pickFor, setPickFor] = useState<Document | null>(null);
  const [timelineFor, setTimelineFor] = useState<Document | null>(null);
  // Local draft so the user can type freely without spamming a request on
  // every keystroke; it's committed to filters on submit (or on Clear).
  const [draftQ, setDraftQ] = useState(filters.q);
  useEffect(() => { setDraftQ(filters.q); }, [filters.q]);

  async function remove(id: number) {
    if (!confirm("Remove this document? It will no longer appear in search results or new chats, but existing citations and timeline links will still work.")) return;
    await api.del(`/cases/${caseId}/documents/${id}`);
    onChanged();
  }

  async function retry(id: number) {
    await api.post(`/cases/${caseId}/documents/${id}/reprocess`);
    onChanged();
  }

  function onSearchSubmit(ev: FormEvent) {
    ev.preventDefault();
    onFiltersChange({ q: draftQ });
  }

  function clearFilters() {
    setDraftQ("");
    onFiltersChange({ q: "", status: "all", page: 1 });
  }

  const counts = page?.status_counts;
  const total = page?.total ?? 0;
  const items = page?.items ?? [];
  const pageCount = Math.max(1, Math.ceil(total / pageSize));
  const isFiltered = filters.q.trim() !== "" || filters.status !== "all";
  const hasAnyDocs = (counts?.all ?? 0) > 0;

  // First load shows a thin loading row; the empty case (no docs yet)
  // shouldn't render any of the toolbar/pager chrome — the dropzone above
  // already prompts the user to upload.
  if (!page && !busy) return null;
  if (page && !hasAnyDocs && !isFiltered) return null;

  function countFor(s: DocStatusFilter): number {
    if (!counts) return 0;
    return counts[s];
  }

  return (
    <div className="docs-list">
      <form className="cases-toolbar" role="search" onSubmit={onSearchSubmit}>
        <div className="cases-toolbar__search">
          <label htmlFor="docs-q" className="visually-hidden">Search documents</label>
          <input
            id="docs-q"
            type="search"
            placeholder="Search by file name…"
            value={draftQ}
            onChange={(e) => setDraftQ(e.target.value)}
          />
          <button type="submit" className="btn btn--primary">Search</button>
          {isFiltered && (
            <button type="button" className="btn" onClick={clearFilters}>Clear</button>
          )}
        </div>

        <div className="cases-toolbar__tabs" role="tablist" aria-label="Filter by ingest status">
          {STATUS_TABS.map((t) => {
            const active = filters.status === t.value;
            return (
              <button
                key={t.value}
                type="button"
                role="tab"
                aria-selected={active}
                className={`cases-tab${active ? " cases-tab--active" : ""}`}
                onClick={() => onFiltersChange({ status: t.value })}
              >
                <span>{t.label}</span>
                <span className="cases-tab__count">{countFor(t.value)}</span>
              </button>
            );
          })}
        </div>
      </form>

      <div className="cases-meta muted">
        {busy && !page ? (
          <span>Loading documents…</span>
        ) : total === 0 ? (
          <span>{isFiltered ? "No documents match these filters." : ""}</span>
        ) : (
          <span>
            Showing {((filters.page - 1) * pageSize + 1).toLocaleString()}
            –{Math.min(filters.page * pageSize, total).toLocaleString()} of{" "}
            {total.toLocaleString()}
          </span>
        )}
        {busy && page && <span>Updating…</span>}
      </div>

      {total === 0 ? (
        isFiltered ? (
          <div className="empty">
            <p>No documents match your search. Try a different term or clear the filters.</p>
            <button className="btn" type="button" onClick={clearFilters}>Clear filters</button>
          </div>
        ) : null
      ) : (
        <div className="doc-table-wrap">
          <table className="doc-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Pages</th>
                <th>Chunks</th>
                <th>Status</th>
                <th>Progress</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {items.map((d) => (
                <tr key={d.id}>
                  <td data-label="Name">
                    <button
                      type="button"
                      className="doc-table__name doc-table__name--link"
                      onClick={() => setPickFor(d)}
                      title="Open or download original file"
                    >
                      {d.original_name}
                    </button>
                    <div className="doc-table__sub">
                      {formatBytes(d.size_bytes)}{d.ocr_applied ? " · OCR" : ""}
                      {d.ingest_profile === "large" ? " · Large" : ""}
                    </div>
                    {d.ingest_profile === "large" && d.est_llm_calls != null && (
                      <div
                        className="doc-table__sub"
                        title="Estimated AI work for this document's ingest (timeline + translation + vision). Token counts are estimates, not a bill."
                      >
                        ~{formatNum(d.est_llm_calls)} AI calls · ~{formatNum(d.est_input_tokens ?? 0)} in / ~{formatNum(d.est_output_tokens ?? 0)} out tokens
                      </div>
                    )}
                    {d.translated && (
                      <span
                        className="doc-tag doc-tag--translated"
                        title={
                          d.source_language
                            ? `Translated to English from ${d.source_language} by AI during ingest. The original file is unchanged.`
                            : "Translated to English by AI during ingest. The original file is unchanged."
                        }
                      >
                        AI Translated{d.source_language ? ` · from ${d.source_language}` : ""}
                      </span>
                    )}
                    {!d.translated && d.translate_requested && d.status !== "ready" && d.status !== "failed" && (
                      <span className="doc-tag doc-tag--pending" title="Translation to English will run during processing.">
                        Translation queued
                      </span>
                    )}
                    {d.error_message && <div className="doc-table__err">{d.error_message}</div>}
                  </td>
                  <td data-label="Pages">{d.page_count ?? "—"}</td>
                  <td data-label="Chunks">{d.chunk_count}</td>
                  <td data-label="Status"><span className={`badge badge--${d.status}`}>{d.status}</span></td>
                  <td data-label="Progress">
                    <div className="progress-cell">
                      <div className="progress" title={`${d.progress_pct}%`}>
                        <div className="progress__bar" style={{ width: `${d.progress_pct}%` }} />
                      </div>
                      {d.status !== "ready" && d.status !== "failed" && (
                        <span className="progress-cell__pct">{d.progress_pct}%</span>
                      )}
                    </div>
                  </td>
                  <td data-label="Actions">
                    <div className="doc-table__actions">
                      {d.status === "failed" && (
                        <button className="btn btn--ghost" onClick={() => retry(d.id)}>Retry</button>
                      )}
                      {d.status === "ready" && (
                        <TimelineButton
                          doc={d}
                          caseId={caseId}
                          onBuild={() => setTimelineFor(d)}
                          onSynced={onChanged}
                        />
                      )}
                      <button className="btn btn--ghost" onClick={() => remove(d.id)}>Delete</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {total > 0 && pageCount > 1 && (
        <div className="cases-pager">
          <button
            type="button"
            className="btn"
            disabled={filters.page <= 1 || busy}
            onClick={() => onFiltersChange({ page: Math.max(1, filters.page - 1) })}
          >
            ← Previous
          </button>
          <span className="muted">
            Page {filters.page} of {pageCount}
          </span>
          <button
            type="button"
            className="btn"
            disabled={filters.page >= pageCount || busy}
            onClick={() => onFiltersChange({ page: filters.page + 1 })}
          >
            Next →
          </button>
        </div>
      )}

      {pickFor && (
        <DocOpenModal
          doc={pickFor}
          caseId={caseId}
          onClose={() => setPickFor(null)}
        />
      )}

      {timelineFor && (
        <BuildTimelineModal
          doc={timelineFor}
          caseId={caseId}
          onClose={() => setTimelineFor(null)}
          onStarted={onChanged}
        />
      )}
    </div>
  );
}

/** Per-document timeline control. Reflects the on-demand timeline lifecycle
 *  (idle → queued → running → ready/failed) and self-polls while a build is in
 *  flight so the label updates without a full page refetch. */
function TimelineButton({
  doc,
  caseId,
  onBuild,
  onSynced,
}: {
  doc: Document;
  caseId: number;
  onBuild: () => void;
  onSynced: () => void;
}) {
  // Local mirror so polling can update the label in place. Re-sync if the
  // parent hands us a newer row (e.g. after onSynced refetches the list).
  const [status, setStatus] = useState<Document["timeline_status"]>(doc.timeline_status);
  const [count, setCount] = useState<number>(doc.timeline_event_count ?? 0);
  useEffect(() => {
    setStatus(doc.timeline_status);
    setCount(doc.timeline_event_count ?? 0);
  }, [doc.timeline_status, doc.timeline_event_count]);

  // While queued/running, poll just this document until it settles.
  useEffect(() => {
    if (status !== "queued" && status !== "running") return;
    let cancel = false;
    const t = setInterval(async () => {
      try {
        const fresh = await api.get<Document>(`/cases/${caseId}/documents/${doc.id}`);
        if (cancel) return;
        setStatus(fresh.timeline_status);
        setCount(fresh.timeline_event_count ?? 0);
        if (fresh.timeline_status === "ready" || fresh.timeline_status === "failed") {
          onSynced(); // pull the rest of the list into sync once
        }
      } catch {
        /* transient; keep polling */
      }
    }, 3000);
    return () => { cancel = true; clearInterval(t); };
  }, [status, caseId, doc.id, onSynced]);

  if (doc.chunk_count === 0) return null; // nothing to ground a timeline on

  if (status === "queued") {
    return <button className="btn btn--ghost" disabled title="Timeline build is queued">Timeline queued…</button>;
  }
  if (status === "running") {
    return <button className="btn btn--ghost" disabled title="Building timeline in the background">Building timeline…</button>;
  }
  if (status === "ready") {
    return (
      <button
        className="btn btn--ghost"
        disabled
        title={`Timeline built${doc.timeline_generated_at ? ` ${new Date(doc.timeline_generated_at).toLocaleString()}` : ""}. View it in the Timeline tab.`}
      >
        Timeline ✓{count ? ` (${count})` : ""}
      </button>
    );
  }
  // idle or failed → offer to build (failed shows a retry affordance)
  return (
    <button
      className="btn btn--ghost"
      onClick={onBuild}
      title={status === "failed" ? "Previous build failed — try again" : "Extract a dated event timeline from this document (uses AI)"}
    >
      {status === "failed" ? "Retry timeline" : "Build timeline"}
    </button>
  );
}

/** Confirmation modal that shows the estimated AI work (token counts, not $)
 *  for building this document's timeline, then kicks off a background build. */
function BuildTimelineModal({
  doc,
  caseId,
  onClose,
  onStarted,
}: {
  doc: Document;
  caseId: number;
  onClose: () => void;
  onStarted: () => void;
}) {
  const [est, setEst] = useState<AiCallEstimate | null>(null);
  const [estErr, setEstErr] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    function onKey(ev: KeyboardEvent) {
      if (ev.key === "Escape" && !submitting) onClose();
    }
    document.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [onClose, submitting]);

  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const e = await api.get<AiCallEstimate>(
          `/cases/${caseId}/documents/${doc.id}/timeline-estimate`,
        );
        if (!cancel) setEst(e);
      } catch (e: any) {
        if (!cancel) setEstErr(e?.message ?? "Could not load estimate");
      }
    })();
    return () => { cancel = true; };
  }, [caseId, doc.id]);

  async function confirm() {
    setErr(null);
    setSubmitting(true);
    try {
      await api.post(`/cases/${caseId}/documents/${doc.id}/build-timeline`);
      onStarted();
      onClose();
    } catch (e: any) {
      setErr(e?.message ?? "Could not start timeline build");
      setSubmitting(false);
    }
  }

  return (
    <div
      className="modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby="build-timeline-title"
      onClick={() => { if (!submitting) onClose(); }}
    >
      <div className="modal__card" onClick={(ev) => ev.stopPropagation()}>
        <header className="modal__header">
          <div className="modal__header-main">
            <h2 id="build-timeline-title" className="modal__header-title">Build timeline</h2>
            <div className="modal__header-meta">
              <span className="muted">{doc.original_name}</span>
            </div>
          </div>
          <button
            type="button"
            className="modal__close"
            onClick={onClose}
            disabled={submitting}
            aria-label="Close"
            title="Close (Esc)"
          >
            ×
          </button>
        </header>

        <div className="modal__body">
          <p className="modal__desc">
            This scans the document for dated events and builds a cited chronology you can
            view in the <strong>Timeline</strong> tab. It runs in the background — you can
            leave this page. It uses AI, so here's the estimated work first:
          </p>

          {estErr && <div className="alert alert--error">{estErr}</div>}
          {!est && !estErr && <div className="muted">Estimating…</div>}
          {est && (
            <div className="dropzone__estimate" role="note">
              <strong>~{formatNum(est.total_calls)} AI calls</strong>{" · "}
              ~{formatNum(est.input_tokens)} input / ~{formatNum(est.output_tokens)} output tokens
              {est.capped && " (timeline capped)"}
              <div className="muted" style={{ marginTop: 4 }}>
                Token counts are estimates, not a bill. Chat &amp; search don't need this — they
                already work from the document's text.
              </div>
            </div>
          )}

          {err && <div className="alert alert--error" role="alert">{err}</div>}
        </div>

        <footer className="modal__footer">
          <span className="muted modal__footer-hint">
            {submitting ? "Starting…" : ""}
          </span>
          <div className="modal__footer-actions">
            <button type="button" className="btn" onClick={onClose} disabled={submitting}>
              Cancel
            </button>
            <button
              type="button"
              className="btn btn--primary"
              onClick={confirm}
              disabled={submitting || (!est && !estErr)}
            >
              {submitting ? "Starting…" : "Build timeline"}
            </button>
          </div>
        </footer>
      </div>
    </div>
  );
}

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  if (n < 1024 * 1024 * 1024) return `${(n / 1024 / 1024).toFixed(1)} MB`;
  return `${(n / 1024 / 1024 / 1024).toFixed(1)} GB`;
}

function formatNum(n: number): string {
  if (!Number.isFinite(n)) return "0";
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}k`;
  return `${Math.round(n)}`;
}

/** True for file types that browsers can render inline in a new tab.
 *  Everything else (docx, doc, etc.) would just trigger a download anyway,
 *  so we hide the "Open in new tab" option for those. */
function canPreviewInTab(d: Document): boolean {
  const mime = (d.mime_type || "").toLowerCase();
  if (mime === "application/pdf") return true;
  if (mime.startsWith("text/")) return true;
  if (mime.startsWith("image/")) return true;
  const name = d.original_name.toLowerCase();
  return /\.(pdf|txt|md|markdown|csv|json|log|png|jpe?g|gif|webp|svg)$/.test(name);
}

interface DocOpenModalProps {
  doc: Document;
  caseId: number;
  onClose: () => void;
}

function DocOpenModal({ doc, caseId, onClose }: DocOpenModalProps) {
  const [busy, setBusy] = useState<"open" | "download" | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const previewable = canPreviewInTab(doc);

  useEffect(() => {
    function onKey(ev: KeyboardEvent) {
      if (ev.key === "Escape" && !busy) onClose();
    }
    document.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [onClose, busy]);

  async function fetchBlob(): Promise<Blob> {
    return api.blob(`/cases/${caseId}/documents/${doc.id}/raw`);
  }

  async function openInTab() {
    setErr(null);
    setBusy("open");
    try {
      const blob = await fetchBlob();
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank", "noopener");
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
      onClose();
    } catch (e: any) {
      setErr(e?.message ?? "Could not open file");
    } finally {
      setBusy(null);
    }
  }

  async function download() {
    setErr(null);
    setBusy("download");
    try {
      const blob = await fetchBlob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = doc.original_name;
      a.rel = "noopener";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
      onClose();
    } catch (e: any) {
      setErr(e?.message ?? "Could not download file");
    } finally {
      setBusy(null);
    }
  }

  return (
    <div
      className="modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby="doc-open-modal-title"
      onClick={() => { if (!busy) onClose(); }}
    >
      <div
        className="modal__card modal__card--split"
        onClick={(ev) => ev.stopPropagation()}
      >
        <header className="modal__header">
          <div className="modal__header-main">
            <h2 id="doc-open-modal-title" className="modal__header-title">
              {doc.original_name}
            </h2>
            <div className="modal__header-meta">
              <span className="muted">
                {formatBytes(doc.size_bytes)}
                {doc.mime_type ? ` · ${doc.mime_type}` : ""}
              </span>
            </div>
          </div>
          <button
            type="button"
            className="modal__close"
            onClick={onClose}
            disabled={!!busy}
            aria-label="Close"
            title="Close (Esc)"
          >
            ×
          </button>
        </header>

        <div className="modal__body">
          <p className="modal__desc">How would you like to open this file?</p>
          {!previewable && (
            <p className="muted">
              This file type can't be previewed in the browser, so it will be downloaded.
            </p>
          )}
          {err && <div className="alert alert--error" role="alert">{err}</div>}
        </div>

        <footer className="modal__footer">
          <span className="muted modal__footer-hint">
            {busy === "open" ? "Opening…" : busy === "download" ? "Downloading…" : ""}
          </span>
          <div className="modal__footer-actions">
            <button
              type="button"
              className="btn"
              onClick={onClose}
              disabled={!!busy}
            >
              Cancel
            </button>
            {previewable && (
              <button
                type="button"
                className="btn"
                onClick={openInTab}
                disabled={!!busy}
              >
                Open in new tab
              </button>
            )}
            <button
              type="button"
              className="btn btn--primary"
              onClick={download}
              disabled={!!busy}
            >
              Download
            </button>
          </div>
        </footer>
      </div>
    </div>
  );
}
